// import { newsService } from "./news.service";
import { type SentimentParams, SentimentParamsOptions } from "./news.models";
import type { NewsDO } from "./news.data";
import { capitalized, formatDate } from "../core/formatters";
import { DateOptionSet } from "../core/models/date";
import { i18n } from '../internationalize/i18n.service'

import './news.style.scss'
import type { NewsService } from "./news.service";

export class NewsComponent extends HTMLElement {
    private news: NewsDO[] = []
    private query: string | null = ""
    private tokens: string | null = ""
    private newsTitle: string | null = ""
    private table: HTMLTableElement|undefined

    constructor(private newsService:NewsService = window.tbwServices.newsService) {
        super()
        this.className = 'tbw-news'
    }

    connectedCallback() {
        this.setNewsAttributes()
        this.init()
    }

    async init() {
        const newsRes = await this.load()
        if (!newsRes) { return }
        
        this.news = newsRes

        this.table = document.createElement('table')
        this.table.className = 'tbw-news-table'
        this.createTopRow()

        this.news.sort((a, b) => new Date(b.published).getTime() - new Date(a.published).getTime())
        this.createNewsTableBody()

        this.appendChild(this.table)
        this.attachClickTracking()
        this.i18n()
    }

    i18n() {
        const i18nElements = this.querySelectorAll<HTMLElement>('[i18n]')
        i18nElements.forEach((elem) => {
            const key = elem.getAttribute('i18n');
            if (key) { elem.innerText = i18n.t(key); }
        })
    }

    setNewsAttributes() {
        this.query = this.getAttribute('data-news-query') ?? null
        this.tokens = this.getAttribute('data-news-tokens') ?? null
        this.newsTitle = this.getAttribute('data-news-title') ?? null
    }

    async load() {
        return await this.newsService.load(this.query, this.tokens, this.newsTitle)
    }

    private createTopRow() {
        const HEADER_COLUMNS = [
            i18n.t('news_title'),
            i18n.t('news_published')
        ]
        const topRow = document.createElement('thead')

        topRow.className = 'news-row top-row'
        topRow.innerHTML = `
            <tr>
                ${HEADER_COLUMNS.map(column => `<th class="caption1">${column}</th>`).join('')}
            </tr>
        `

        this.table?.appendChild(topRow)
    }

    private createTimeIcon(): SVGSVGElement {
        const timeIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        timeIcon.setAttribute("width", "12")
        timeIcon.setAttribute("height", "12")
        timeIcon.setAttribute("viewBox", "0 0 16 17")
        timeIcon.setAttribute("fill", "none")

        timeIcon.innerHTML = `
            <path d="M5.24023 2.19952V4.16474" stroke="var(--midnight-blue)" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.4814 2.19952V4.16474" stroke="var(--midnight-blue)" stroke-width="0.74211" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2.29297 6.84399H13.4292" stroke="var(--midnight-blue)" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M13.7561 6.45749V12.0256C13.7561 13.9908 12.7735 15.301 10.4808 15.301H5.24021C2.94745 15.301 1.96484 13.9908 1.96484 12.0256V6.45749C1.96484 4.49227 2.94745 3.18213 5.24021 3.18213H10.4808C12.7735 3.18213 13.7561 4.49227 13.7561 6.45749Z" stroke="var(--midnight-blue)" stroke-width="1.07194" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        `
        return timeIcon
    }

    getSourceName(link: string) {
        const extractFromURL = (url: string) => {
            try {
                const domain = new URL(url).hostname.replace('www.', '')
                return domain.split('.')[0]
            } catch {
                return url
            }
        }
        return capitalized(extractFromURL(link))
    }

    private createNewsTableBody() {
        const tableBody = document.createElement('tbody')
        tableBody.className = 'tbw-news-table-body'

        this.news.forEach(news => {
            const newsRow = document.createElement('tr')

            const sentiment: SentimentParams = SentimentParamsOptions[news.sentiment] ?? undefined
            const sentimentIcon = sentiment ? `<img src="${sentiment.icon}" alt="Sentiment Icon" class='news-row-icon'>` : `<div class='news-row-icon'></div>`
            const dateLanguage = i18n.getLanguage() === 'en' ? DateOptionSet.dateEN : DateOptionSet.dateFR
            const publishedDate = formatDate(new Date(news.published), dateLanguage)
            const sourceDomain = news.link.split('/')[2]

            newsRow.innerHTML = `
                <td>
                    <a href="${news.link}" target="_blank" class='is-link' data-news-source="${sourceDomain}">
                        ${sentimentIcon}
                        <div class='news-row-content'>
                            ${news.source_logo ? `<img src="${news.source_logo}" alt="News Image" class='news-row-logo'>` : '<div class="news-row-logo"></div>'}
                            <p class="caption1">${news.title}</p>
                        </div>
                    </a>
                </td>
                <td>
                    <div class='news-date-container'>${this.createTimeIcon().outerHTML}<p class="caption2">${publishedDate}</p></div>
                </td>
            `

            tableBody.appendChild(newsRow)
        })
        this.table?.appendChild(tableBody)
    }

    private attachClickTracking() {
        this.table?.addEventListener('click', (event) => {
            const target = event.target as HTMLElement
            const link = target.closest('a')
            if (!link) { return }
            const newsSource = link.getAttribute("data-news-source") ?? null
            window.tbwServices?.analyticsService?.track?.('news_agg_click', { news_source: newsSource })
        })
    }
}
